import { createSelector } from '@reduxjs/toolkit';
import { useFormikContext } from 'formik';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { VisibilityComparator, VisibilityRuleType } from 'src/constants';
import { useFeatureFlags } from 'src/context/flagsContext';
import { PortalConfigContext } from 'src/context/portalConfigContext';
import { RecipientSelector } from 'src/features/recipient-selector/RecipientSelector';
import { useAppSelector } from 'src/hooks/useStore';
import { RuleField } from 'src/legacy/components/Settings/VisibilityRecipientSelector';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import { RootState } from 'src/store';
import {
  CustomFieldEntityType,
  CustomFieldType,
} from 'src/store/clients/types';
import {
  CHECKBOX_CHECKED_VALUE,
  type ComparatorField,
  CustomVisibilitySchema,
  expandLegacyValuesToValueItems,
  getComparatorRules,
  isCustomFieldRule,
  type VisibilityRecipient,
} from 'src/utils/app_visibility';
import { ensureUnreachable } from 'src/utils/common_utils';

const VISIBILITY_RULE_FIELD_TYPES: CustomFieldType[] = [
  'multiSelect',
  'select',
  'checkbox',
];

const userSelector = createSelector(
  [
    (state: RootState) => state.clients.companies,
    (state: RootState) => state.clients.activeClients,
  ],
  (allCompanies, clients) => ({ allCompanies, clients }),
);

export function useVisibilityRuleFields(ruleIndex: number) {
  const { t } = useTranslation();
  const { values, setFieldValue, handleChange, errors, touched } =
    useFormikContext<CustomVisibilitySchema>();

  const rule = values?.visibilityConfig?.[ruleIndex];
  const { allCompanies, clients } = useAppSelector(userSelector);
  const clientCustomFields = useCustomFields(CustomFieldEntityType.CLIENT);
  const companyCustomFields = useCustomFields(CustomFieldEntityType.COMPANY);
  const portalConfig = useContext(PortalConfigContext);
  const areCompaniesEnabled = !portalConfig.features.disableCompanies;
  const { EnableFeatureAppVisibility: isAppVisibilityFeatureEnabled } =
    useFeatureFlags();

  const clientTagsCustomFields = useMemo(
    () =>
      Object.values(clientCustomFields || {}).filter((field) =>
        VISIBILITY_RULE_FIELD_TYPES.includes(field.type),
      ),
    [clientCustomFields],
  );
  const companyTagsCustomFields = useMemo(
    () =>
      Object.values(companyCustomFields || {}).filter((field) =>
        VISIBILITY_RULE_FIELD_TYPES.includes(field.type),
      ),
    [companyCustomFields],
  );

  const clientOptions = useMemo(
    () =>
      clients
        .filter((x) => !!x.fields.givenName && !!x.fields.familyName)
        .map((client) => ({
          id: client.id,
          label: `${client.fields.givenName} ${client.fields.familyName}`,
          color: client.fields.fallbackColor,
        })),
    [clients],
  );

  const companiesOptions = useMemo(
    () =>
      allCompanies
        .filter(
          (company) =>
            !!company.fields.name && company.entityStatus === 'ENABLED',
        )
        .map((company) => ({
          id: company.id,
          label: company.fields.name,
          color: company.fields.fallbackColor ?? '',
        })),
    [allCompanies],
  );

  const clientCompanyFields: ComparatorField[] = useMemo(
    () => [
      {
        id: 'client',
        label: t(($) => $.client, { count: 1 }),
        type: VisibilityRuleType.Client,
        isCompanyCustomField: false,
      },
      ...(areCompaniesEnabled
        ? [
            {
              id: 'company',
              label: t(($) => $.company, { count: 1 }),
              type: VisibilityRuleType.Company,
              isCompanyCustomField: false,
            },
          ]
        : []),
    ],
    [areCompaniesEnabled, t],
  );

  const clientCustomFieldsDropdown = useMemo(
    () =>
      clientTagsCustomFields.map((field) => ({
        id: field.id,
        label: field.name,
        type: VisibilityRuleType.CustomField as const,
        isCompanyCustomField: false,
      })),
    [clientTagsCustomFields],
  );

  const companyCustomFieldsDropdown = useMemo(
    () =>
      areCompaniesEnabled
        ? companyTagsCustomFields.map((field) => ({
            id: field.id,
            label: field.name,
            type: VisibilityRuleType.CustomField as const,
            isCompanyCustomField: true,
          }))
        : [],
    [areCompaniesEnabled, companyTagsCustomFields],
  );

  const allFields = useMemo(
    () => [
      ...clientCompanyFields,
      ...clientCustomFieldsDropdown,
      ...companyCustomFieldsDropdown,
    ],
    [
      clientCompanyFields,
      clientCustomFieldsDropdown,
      companyCustomFieldsDropdown,
    ],
  );

  const getComparatorFieldValues = useMemo(
    () =>
      <T extends VisibilityRuleType>(
        comparatorFieldType: T,
        comparatorFieldTypeId: T extends VisibilityRuleType.CustomField
          ? string
          : undefined,
      ) => {
        switch (comparatorFieldType) {
          case VisibilityRuleType.NoVisibility:
            return { id: VisibilityRuleType.NoVisibility, options: [] };
          case VisibilityRuleType.Client:
            return { id: VisibilityRuleType.Client, options: clientOptions };
          case VisibilityRuleType.Company:
            return {
              id: VisibilityRuleType.Company,
              options: companiesOptions,
            };
          case VisibilityRuleType.CustomField: {
            const customFieldOptions =
              clientTagsCustomFields.find((x) => x.id === comparatorFieldTypeId)
                ?.options ||
              companyTagsCustomFields.find(
                (x) => x.id === comparatorFieldTypeId,
              )?.options ||
              [];
            return { id: comparatorFieldTypeId!, options: customFieldOptions };
          }
          default:
            return ensureUnreachable(comparatorFieldType);
        }
      },
    [
      clientOptions,
      companiesOptions,
      clientTagsCustomFields,
      companyTagsCustomFields,
    ],
  );

  const getInitialCustomFieldValues = React.useCallback(() => {
    const comparatorFieldId = isCustomFieldRule(rule)
      ? rule.customFieldId
      : undefined;
    return getComparatorFieldValues(rule.ruleType, comparatorFieldId);
  }, [rule, getComparatorFieldValues]);

  const [selectedFieldValues, setSelectedFieldValues] =
    React.useState<RuleField>(() => getInitialCustomFieldValues());

  const clientSelectedRecipients = useMemo<{
    recipients: VisibilityRecipient[];
  }>(() => {
    if (
      !isAppVisibilityFeatureEnabled ||
      rule.ruleType !== VisibilityRuleType.Client
    )
      return { recipients: [] };

    const resolvedItems =
      rule.valueItems && rule.valueItems.length > 0
        ? rule.valueItems
        : expandLegacyValuesToValueItems(rule.values ?? [], clients);

    const recipients: VisibilityRecipient[] = [];
    for (const item of resolvedItems) {
      const client = clients.find((c) => c.id === item.userId);
      if (!client) continue;
      if (item.companyId) {
        if (client.fields.companyIds?.includes(item.companyId)) {
          recipients.push({
            clientId: item.userId,
            companyId: item.companyId,
            type: 'client',
          });
        }
      } else {
        recipients.push({
          clientId: item.userId,
          companyId: '',
          type: 'client',
        });
      }
    }
    return { recipients };
  }, [rule, clients, isAppVisibilityFeatureEnabled]);

  const handleClientRecipientChange: React.ComponentProps<
    typeof RecipientSelector
  >['onRecipientSelected'] = React.useCallback(
    (inputValues) => {
      const newValueItems = inputValues.map((v) => ({
        userId: v.id,
        ...(v.companyId ? { companyId: v.companyId } : {}),
      }));
      setFieldValue(`visibilityConfig.${ruleIndex}.valueItems`, newValueItems);
      const uniqueUserIds = [...new Set(inputValues.map((v) => v.id))];
      setFieldValue(`visibilityConfig.${ruleIndex}.values`, uniqueUserIds);
    },
    [setFieldValue, ruleIndex],
  );

  React.useEffect(() => {
    if (!clientCustomFields && !companyCustomFields) return;
    setSelectedFieldValues(getInitialCustomFieldValues());
  }, [clientCustomFields, companyCustomFields, getInitialCustomFieldValues]);

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const comparatorFieldId = event.target.value;
    const selectedComparatorField = allFields.find(
      (field) => field.id === comparatorFieldId,
    );
    if (selectedComparatorField) {
      setFieldValue(
        `visibilityConfig.${ruleIndex}.ruleType`,
        selectedComparatorField.type,
      );
      setFieldValue(
        `visibilityConfig.${ruleIndex}.isCompanyCustomField`,
        selectedComparatorField.isCompanyCustomField,
      );
      const comparatorFieldValues = getComparatorFieldValues(
        selectedComparatorField.type,
        comparatorFieldId,
      );
      setSelectedFieldValues(comparatorFieldValues);
      const selectedCustomField = [
        ...clientTagsCustomFields,
        ...companyTagsCustomFields,
      ].find((field) => field.id === comparatorFieldId);
      const isCheckbox = selectedCustomField?.type === 'checkbox';
      // A checkbox rule has no values picker: its value is always "checked".
      setFieldValue(
        `visibilityConfig.${ruleIndex}.values`,
        isCheckbox ? [CHECKBOX_CHECKED_VALUE] : [],
      );
      // Checkbox only offers is / is not checked and select hides "includes all of",
      // so a comparator the new field can't show would otherwise be saved unseen.
      const isSelect = selectedCustomField?.type === 'select';
      if (
        isCheckbox ||
        (isSelect && rule.comparator === VisibilityComparator.IncludesAllOf)
      ) {
        setFieldValue(
          `visibilityConfig.${ruleIndex}.comparator`,
          VisibilityComparator.IncludesAnyOf,
        );
      }
      if (selectedComparatorField.type === VisibilityRuleType.CustomField) {
        setFieldValue(
          `visibilityConfig.${ruleIndex}.customFieldId`,
          comparatorFieldId,
        );
      }
    }
  };

  const errorContainer = errors.visibilityConfig?.[ruleIndex];
  const optionDropdownHelperText =
    !!touched.visibilityConfig?.[ruleIndex]?.values &&
    typeof errorContainer !== 'string' &&
    errorContainer?.values;

  const {
    comparator: selectedComparatorValue,
    ruleType: selectedRuleType,
    values: selectedValues = [],
  } = values.visibilityConfig[ruleIndex];

  const selectedCustomFieldType = useMemo(() => {
    if (!isCustomFieldRule(rule)) return undefined;
    return [...clientTagsCustomFields, ...companyTagsCustomFields].find(
      (field) => field.id === rule.customFieldId,
    )?.type;
  }, [rule, clientTagsCustomFields, companyTagsCustomFields]);

  const comparatorDropdownRules = useMemo(
    () =>
      getComparatorRules(
        selectedRuleType,
        selectedValues,
        selectedCustomFieldType,
      ),
    [selectedRuleType, selectedValues, selectedCustomFieldType],
  );

  return {
    handleChange,
    clients,
    clientCompanyFields,
    clientCustomFieldsDropdown,
    companyCustomFieldsDropdown,
    selectedFieldValues,
    handleFieldChange,
    comparatorDropdownRules,
    isCheckboxField: selectedCustomFieldType === 'checkbox',
    selectedComparatorValue,
    selectedRuleType,
    selectedValues,
    clientSelectedRecipients,
    handleClientRecipientChange,
    optionDropdownHelperText,
    hasFieldError:
      !!touched.visibilityConfig?.[ruleIndex]?.values &&
      !!optionDropdownHelperText,
    isAppVisibilityFeatureEnabled,
    values,
    ruleIndex,
  };
}
